import { Component, effect, inject, input, signal } from '@angular/core';
import {
  BudgetOptions,
  ExpenditureOptions,
} from '../../../../../core/constants-repository/project-calculation.constants';
import { TimeBlockWithTask } from '../../../../../shared/data-types/shared-time-block-types';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { ExpenditureCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/expenditure-calculation.service';
import { ProjectDataService } from '../../state-and-data-handling/project-data.service';
import { BudgetCalculationService } from '../../project-edit/manage-project/project-calculation-form/services/budget-calculation.service';
import { TimeBlockDurationCalculationService } from '../../../../../features/calendar/components/time-block/calculation/duration/time-block-duration-calculation.service';
import { MemberDetailGridRow } from '../../../shared/project-detail-types';

import { TimeFormatType } from '../../../../../core/pipes/time-format.pipe';
import { ProjectMonetaryCalculationService } from '../services/project-monetary-calculation.service';

@Component({
  selector: 'app-project-team-summary',
  templateUrl: './project-team-summary.component.html',
  standalone: false,
})
export class ProjectTeamSummaryComponent {
  timeBlocksWithTask = input.required<readonly TimeBlockWithTask[]>();
  gridData = signal<MemberDetailGridRow[]>([]);

  // Constants
  public readonly BudgetOptions = BudgetOptions;
  public readonly TimeFormatType = TimeFormatType;
  public readonly ExpenditureRateOptions = ExpenditureOptions;

  // DI
  private readonly expenditureCalculatorService = inject(ExpenditureCalculationService);
  private readonly projectDataService = inject(ProjectDataService);
  private readonly budgetCalculationService = inject(BudgetCalculationService);
  private readonly timeBlockDurationCalculationService = inject(
    TimeBlockDurationCalculationService,
  );
  private readonly projectMonetaryCalculationService = inject(ProjectMonetaryCalculationService);

  constructor() {
    effect(() => {
      const blocks = this.timeBlocksWithTask() ?? [];
      this.gridData.set(this.createGridData(blocks));
    });
  }

  public get project(): ProjectModel {
    return this.projectDataService.getProjectClone();
  }

  private createGridData(timeBlocksWithTask: readonly TimeBlockWithTask[]): MemberDetailGridRow[] {
    const gridData: MemberDetailGridRow[] = [];
    const timeBlocksWithTaskForMember = (memberId: number) =>
      timeBlocksWithTask.filter(
        (timeBlockWithTask) => timeBlockWithTask.timeBlock.timeBlockModel.userId === memberId,
      );

    const projectMembers = this.project.projectMemberPropertiesWrapper;
    projectMembers.forEach((projectMemberWrapper) => {
      const timeBlocksWithTask = timeBlocksWithTaskForMember(projectMemberWrapper.member.id);
      gridData.push({
        member: projectMemberWrapper.member,
        spentSeconds: this.calcSpentSeconds(timeBlocksWithTask),
        spentFees: this.expenditureCalculatorService.calcFeeSumForTimeBlock(
          this.project,
          timeBlocksWithTask,
        ),
        budget: this.budgetCalculationService.getBudgetForMember(
          this.project,
          projectMemberWrapper.member.id,
        ),
        costs: this.calcPersonnelCosts(timeBlocksWithTask),
      });
    });
    return gridData;
  }

  private calcSpentSeconds(timeBlocksWithTask: TimeBlockWithTask[]): number {
    const timeBlocks = timeBlocksWithTask.map((timeBlock) => timeBlock.timeBlock);
    return this.timeBlockDurationCalculationService.calculateTimeBlockTimeSum(timeBlocks, true);
  }

  private calcPersonnelCosts(timeBlocksWithTask: TimeBlockWithTask[]): number {
    return this.projectMonetaryCalculationService.calcPersonnelCosts(timeBlocksWithTask);
  }
}
